import { ArrowRightOnRectangleIcon, Bars3Icon, CursorArrowRaysIcon, HomeIcon } from "@heroicons/react/24/outline";
import { UserCircleIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { Link, useLocation } from "react-router-dom";
import { FC, useEffect, useState } from "react";
import { Fab, Typography } from "@mui/material";
import { useAuth } from "react-oidc-context";
import { Drawer } from "antd";
import dayjs from "dayjs";

import LogoVisagioBranco from "../../../assets/images/GrupoVisagio_Vetor.svg";
import { ApprovalDeadline } from "../../../@types/ApprovalDeadline";
import ProgressIcon from "../../../assets/images/progressIcon.svg";
import { fetchDeadline } from "../../../service/deadline";
import Collaborator from "../../../@types/Collaborator";

interface AccountInfo {
  account?: Collaborator;
}

const Header: FC<AccountInfo> = ({ account }) => {
  const location = useLocation();
  const [approvalDeadline, setApprovalDealine] = useState<ApprovalDeadline>();
  const [open, setOpen] = useState(false);
  const auth = useAuth();

  const getCaracterInitial = (name: string) => {
    const arrName = name.split(" ");
    return arrName[0][0] + arrName[arrName.length - 1][0];
  };

  const handleFetchApprovalDeadline = () => {
    fetchDeadline().then((response) => {
      setApprovalDealine(response);
    });
  };

  useEffect(() => {
    handleFetchApprovalDeadline();
  }, []);

  return (
    <nav className="bg-vcolor h-16 px-2 laptop:px-4 py-4 flex justify-between text-sm">
      <div className="flex flex-row flex-1 items-center text-center">
        <img src={LogoVisagioBranco} alt="Logo" className="w-20 laptop:w-24 h-10 laptop:h-12 mr-3 laptop:m-4" />

        <ul
          className={`flex-1 gap-2 laptop:gap-3 laptop:ml-4 ${
            account?.financialSupport || account?.isApprover ? "justify-between" : "justify-center"
          } laptop-max:hidden laptop:flex items-center`}
        >
          {(account?.financialSupport || account?.isApprover) && (
            <li>
              <Link
                to="/"
                className={`text-white text-sm laptop:text-base hover:underline whitespace-nowrap ${
                  location.pathname === "/" ? "underline" : ""
                }`}
              >
                Solicitações
              </Link>
            </li>
          )}

          {(account?.financialSupport || account?.isApprover) && (
            <>
              <li>
                <Link
                  to="/approvals"
                  className={`text-white text-sm laptop:text-base hover:underline whitespace-nowrap ${
                    location.pathname === "/approvals" ? "underline" : ""
                  }`}
                >
                  Aprovações
                </Link>
              </li>
              <li>
                <Link
                  to="/search"
                  className={`text-white text-sm laptop:text-base hover:underline whitespace-nowrap ${
                    location.pathname === "/search" ? "underline" : ""
                  }`}
                >
                  Pesquisa
                </Link>
              </li>
            </>
          )}
        </ul>
      </div>

      <div className="flex-row items-center w-1/3 justify-end laptop-max:hidden desktop:flex desktop:flex-1">
        <div className="flex flex-row items-center justify-end w-full" style={{ height: "100%" }}>
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              color: "white",
              fontSize: "0.600rem",
              padding: 1,
            }}
          >
            <img src={ProgressIcon} alt="Logo" className="w-3 h-3 mr-2" />
            {(approvalDeadline &&
              `Corte será em ${dayjs(new Date(approvalDeadline.deadline).toString()).format("DD/MM/YYYY, H:m")}`) ||
              ""}
          </Typography>
          <div className="text-left">
            <Fab
              aria-label="save"
              sx={{
                backgroundColor: "#0C7878",
                color: "white",
                width: 30,
                height: 30,
                minHeight: 30,
                "&:hover": {
                  backgroundColor: "#033A3FCC",
                  color: "white",
                },
              }}
            >
              {account && getCaracterInitial(account.name)}
            </Fab>
          </div>

          <button
            onClick={() => void auth.removeUser()}
            className="flex flex-row items-end text-white hover:underline pl-2"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5 text-custom-color" />
          </button>
        </div>
      </div>

      {/* MOBILE */}
      {account && (
        <div className="flex flex-row flex-1 items-center justify-end laptop:hidden">
          <button onClick={() => setOpen(true)}>
            <Bars3Icon className="w-8 h-8 text-custom-color" />
          </button>
        </div>
      )}

      <Drawer
        closeIcon={<XMarkIcon className="w-5 h-5 text-white" />}
        title={
          <div className="flex items-center mr-2 h-12">
            <UserCircleIcon className="w-5 h-5 text-custom-color" />
            <span className="text-white text-sm ml-2">{account?.login}</span>
          </div>
        }
        footer={
          <div onClick={() => void auth.removeUser()} className="flex items-center mr-2">
            <ArrowRightOnRectangleIcon className="w-5 h-5 text-red-600" />
            <span className="text-red-600 text-sm ml-2">Sair</span>
          </div>
        }
        placement="right"
        onClose={() => setOpen(false)}
        open={open}
        styles={{
          header: {
            display: "flex",
            background: "#00525d",
          },
          mask: {
            backdropFilter: "blur(10px)",
          },
        }}
      >
        <ul className="flex flex-col space-y-4 py-2">
          <li>
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                location.pathname === "/" && !location.search.includes("isAdvance")
                  ? "bg-[#0C7878]/10 text-[#0C7878]"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <HomeIcon className="w-5 h-5" />
              <span className="font-medium">Reembolsos</span>
            </Link>
          </li>
          <li>
            <Link
              to="/?isAdvance=true"
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                location.pathname === "/" && location.search.includes("isAdvance")
                  ? "bg-[#0C7878]/10 text-[#0C7878]"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <CursorArrowRaysIcon className="w-5 h-5" />
              <span className="font-medium">Adiantamentos</span>
            </Link>
          </li>

          {(account?.financialSupport || account?.isApprover) && (
            <>
              <li>
                <Link
                  to="/approvals"
                  onClick={() => {
                    setOpen(false);
                  }}
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                    location.pathname === "/approvals"
                      ? "bg-[#0C7878]/10 text-[#0C7878]"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <CursorArrowRaysIcon className="w-5 h-5" />
                  <span className="font-medium">Aprovações</span>
                </Link>
              </li>

              {window.innerWidth >= 1024 && (
                <li>
                  <Link
                    to="/search"
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                      location.pathname === "/search"
                        ? "bg-[#0C7878]/10 text-[#0C7878]"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <CursorArrowRaysIcon className="w-5 h-5" />
                    <span className="font-medium">Pesquisa</span>
                  </Link>
                </li>
              )}
            </>
          )}
        </ul>
      </Drawer>
    </nav>
  );
};

export default Header;
